import * as React from 'react';

import Input from 'components/inputs/input/Input';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';

import ContentApprovalLookItem from 'modules/campaigns/components/content-approval/ContentApprovalLookItem';

import { __t, applyTranslations } from 'utils/language';
import { prepareDeliverableAttachmentsForGallery } from 'utils/deliverables';

import type { ClientApprovalBucket, Data, TextEntry, TextEntryKeyboardEvent } from 'types';

import './content-approval-look.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./content-approval-look.i18n.json'));

type Props = {
  bucket: ClientApprovalBucket,
  onDeleteItem: (b: number, a: number) => void,
  onDeleteBucket: (a: number) => void,
  onEditNote: (b: number, a: string) => void,
  onUpdateTitle: (b: number, a: string) => void
};

type State = {
  title: {
    [x: number]: {
      value: string,
      editable: boolean
    }
  }
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

class ContentApprovalLook extends React.PureComponent<Props, State> {
  state = {
    title: {
      [this.props.bucket.id]: {
        value: this.props.bucket.title,
        editable: this.props.bucket.id < 0
      }
    }
  };

  handleDeleteItem = (event: SyntheticEvent<HTMLElement>): void => {
    const { bucket, onDeleteItem } = this.props;
    const target = event.currentTarget;
    const itemId = Number(target.id);

    onDeleteItem(bucket.id, itemId);
  };

  handleEditNote = (event: SyntheticEvent<TextEntry>): void => {
    const { bucket, onEditNote } = this.props;
    const target = event.currentTarget;

    onEditNote(bucket.id, target.value);
  };

  handleToggleEditTitle = (bucketId: number): void => {
    if (bucketId) {
      this.setState(state => ({
        title: {
          [bucketId]: {
            ...state.title[bucketId],
            editable: !state.title[bucketId].editable
          }
        }
      }));
    }
  };

  handleToggleEditTitleContextMenu = (bucketId: number): void => {
    this.handleToggleEditTitle(bucketId);
  };

  handleToggleEditTitleElement = (event: SyntheticEvent<HTMLElement>): void => {
    const target = event.currentTarget;
    const bucketId = Number(target.dataset.bucketId) || 0;

    this.handleToggleEditTitle(bucketId);
  };

  handleEditTitle = (event: SyntheticEvent<TextEntry>): void => {
    const target = event.currentTarget;
    const bucketId = Number(target.dataset.bucketId) || 0;

    if (bucketId) {
      this.setState(state => ({
        title: {
          [bucketId]: {
            ...state.title[bucketId],
            value: target.value
          }
        }
      }));
    }
  };

  handleUpdateTitle = (bucketId: number, value: string): void => {
    const { onUpdateTitle } = this.props;

    this.handleToggleEditTitle(bucketId);

    if (bucketId) {
      this.setState(state => ({
        title: {
          [bucketId]: {
            ...state.title[bucketId],
            editable: false
          }
        }
      }));

      if (value) {
        onUpdateTitle(bucketId, value);
      }
    }
  };

  handleBlur = (event: SyntheticEvent<TextEntry>, data: Data): void => {
    const bucketId = data && Number(data.bucketId);
    const value = (data && data.value) || '';

    if (bucketId) {
      this.handleUpdateTitle(bucketId, value);
    }
  };

  handleKeyPress = (event: TextEntryKeyboardEvent, data: Data): void => {
    if (event.key.includes('Enter')) {
      const bucketId = data && Number(data.bucketId);
      const value = (data && data.value) || '';

      this.handleUpdateTitle(bucketId, value);
    }
  };

  renderContextMenu(): React.ReactNode {
    const { bucket, onDeleteBucket } = this.props;
    const messageKey = 'content_approval_look.context_menu';

    const contextMenuItems = [
      {
        name: 'edit-look',
        text: __t(`${messageKey}.edit`),
        onClick: () => this.handleToggleEditTitleContextMenu(bucket.id)
      },
      {
        name: 'delete-look',
        text: __t(`${messageKey}.delete`),
        onClick: () => onDeleteBucket(bucket.id)
      }
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        iconHeight={12}
        customColor="black"
        contextMenuProps={{ items: contextMenuItems }}
      />
    );
  }

  renderHeader(): React.ReactNode {
    const { bucket } = this.props;
    const { title } = this.state;
    const bucketId = bucket.id;

    return (
      <h2 className="content-approval-look__header">
        <div
          className="content-approval-look__title"
          title={title[bucketId].value}
        >
          {
            title[bucketId].editable ?
              <Input
                key={`content-approval-look__header-${bucketId}`}
                name="text"
                width="responsive"
                value={title[bucketId].value}
                autoFocus={title[bucketId].editable}
                size={title[bucketId].value.length}
                className="content-approval-look__input"
                onChange={this.handleEditTitle}
                onBlur={this.handleBlur}
                onKeyPress={this.handleKeyPress}
                data={{
                  'bucket-id': bucketId,
                  'value': title[bucketId].value // quote it to satisfy eslint
                }}
              /> :
              <div
                className="content-approval-look__title-text text-overflow-ellipsis"
                onClick={this.handleToggleEditTitleElement}
                data-bucket-id={bucketId}
              >
                {title[bucketId].value}
              </div>
          }

        </div>
        <div className="content-approval-look__context-menu">
          {this.renderContextMenu()}
        </div>
      </h2>
    );
  }

  renderItems(): React.ReactNode {
    const { bucket } = this.props;
    const galleryItems = prepareDeliverableAttachmentsForGallery(bucket.items);

    return (
      <div className="content-approval-look__items">
        {
          bucket.items.map((item, index) => (
            <ContentApprovalLookItem
              key={`content-approval-look-${item.id}`}
              item={item}
              lookName={bucket.title}
              galleryItems={galleryItems}
              galleryItemIndex={index}
              onDeleteItem={this.handleDeleteItem}
            />
          ))
        }
      </div>
    );
  }

  render(): React.ReactNode {
    const { bucket } = this.props;

    return (
      <React.Fragment>
        <div className="content-approval-look">
          <div className="content-approval-look__container">
            {this.renderHeader()}
            {this.renderItems()}
            <div className="content-approval-look__footer" />
          </div>
        </div>
        <div className="content-approval-look__note-section">
          <div className="content-approval-look__note">
            <h4 className="content-approval-look__note-title">
              {__t('content_approval_look.note_text')}
            </h4>
            <Input
              name="text"
              width="extra-wide"
              value={bucket.notes.accountManager || ''}
              multiline
              onChange={this.handleEditNote}
            />
          </div>
          <div className="content-approval-look__note">
            <h4 className="content-approval-look__note-title">
              {__t('content_approval_look.client_note_text')}
            </h4>
            <div className="content-approval-look__note-text">
              {bucket.notes.brandRepresentative || '–'}
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default ContentApprovalLook;
